import { useNavigate, type NavigateFunction } from "react-router-dom";
import { useLocation, useRoutes } from "react-router-dom";
import { useEffect } from "react";
import routes from "./config";

let navigateResolver: (navigate: ReturnType<typeof useNavigate>) => void;

declare global {
  interface Window {
    REACT_APP_NAVIGATE: ReturnType<typeof useNavigate>;
  }
}

export const navigatePromise = new Promise<NavigateFunction>((resolve) => {
  navigateResolver = resolve;
});

export function AppRoutes() {
  const element = useRoutes(routes);

  // Start each page at the top. React Router restores nothing on its own, so
  // navigating from halfway down a long page opened the next one halfway down —
  // which looks like the site is broken, not like a setting is missing. A hash
  // is a request for a specific place, so go there instead of to the top.
  //
  // The browser cannot make that jump itself when the hash arrives with a fresh
  // load of the app — /#visit typed in, or followed from /wholesale — because
  // the section does not exist yet on the frame the URL lands on. So look for
  // it, and keep looking for a few frames while React renders the page.
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }
    const id = decodeURIComponent(hash.slice(1));
    let frames = 0;
    let raf = 0;
    const jump = () => {
      const target = document.getElementById(id);
      if (target) {
        target.scrollIntoView();
        return;
      }
      if (frames++ < 60) raf = requestAnimationFrame(jump);
    };
    jump();
    return () => cancelAnimationFrame(raf);
  }, [pathname, hash]);
  const navigate = useNavigate();
  useEffect(() => {
    window.REACT_APP_NAVIGATE = navigate;
    navigateResolver(window.REACT_APP_NAVIGATE);
  });
  return element;
}
